import { LEAVE_STATUSES, type LeaveStatus } from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { apiList, api } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  Notice,
  Pager,
  SchoolSelect,
  errorMessage,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { t, type MessageKey } from '../../i18n';

interface LeaveRequest {
  id: string;
  studentName: string;
  admissionNumber: string;
  fromDate: string;
  toDate: string;
  reason: string;
  status: LeaveStatus;
  reviewNote: string | null;
}

/** Staff queue of student leave requests, pending first. */
export function LeavePage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [schoolId, setSchoolId] = useState('');
  const [status, setStatus] = useState<LeaveStatus | ''>('pending');
  const [page, setPage] = useState(1);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const canReview = !!schoolId && can('leave_requests.review', schoolId);

  const list = useQuery({
    queryKey: ['leave', schoolId, status, page],
    queryFn: () =>
      apiList<LeaveRequest>(`/schools/${schoolId}/leave-requests`, {
        query: { status, page, pageSize: 20 },
      }),
    enabled: !!schoolId,
  });
  const review = useMutation({
    mutationFn: (args: { id: string; decision: 'approved' | 'rejected' }) =>
      api(`/schools/${schoolId}/leave-requests/${args.id}/review`, {
        method: 'POST',
        body: { decision: args.decision, note: notes[args.id] || null },
      }),
    onSuccess: () => {
      setMessage(null);
      void queryClient.invalidateQueries({ queryKey: ['leave', schoolId] });
    },
    onError: (e) => setMessage(errorMessage(e)),
  });

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <h1 className="text-2xl font-bold">{t('leave.title')}</h1>
      <div className="flex flex-wrap items-end gap-4">
        <SchoolSelect value={schoolId} onChange={setSchoolId} />
        <div>
          <label htmlFor="leave-status" className="block text-sm font-medium">
            {t('leave.status')}
          </label>
          <select
            id="leave-status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as LeaveStatus | '');
              setPage(1);
            }}
            className={selectClass}
          >
            <option value="">{t('leave.all')}</option>
            {LEAVE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`leave.status.${s}` as MessageKey)}
              </option>
            ))}
          </select>
        </div>
      </div>
      {message && <Notice kind="error">{message}</Notice>}
      {list.isError && <Notice kind="error">{errorMessage(list.error)}</Notice>}
      {list.data?.data.length === 0 && <p className="text-slate-600">{t('leave.empty')}</p>}
      <ul className="space-y-3">
        {list.data?.data.map((r) => (
          <li key={r.id} className="rounded-lg border border-slate-200 bg-white p-4 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-semibold">
                {r.studentName} <span className="font-mono text-xs">{r.admissionNumber}</span>
              </h2>
              <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium">
                {t(`leave.status.${r.status}` as MessageKey)}
              </span>
            </div>
            <p className="mt-1 text-slate-700">
              {r.fromDate === r.toDate ? r.fromDate : `${r.fromDate} → ${r.toDate}`} — {r.reason}
            </p>
            {r.reviewNote && <p className="mt-1 text-slate-600">{r.reviewNote}</p>}
            {canReview && r.status === 'pending' && (
              <div className="mt-3 flex flex-wrap items-end gap-2">
                <label className="flex-1 text-xs font-medium">
                  {t('leave.note')}
                  <input
                    value={notes[r.id] ?? ''}
                    maxLength={500}
                    onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })}
                    className="mt-1 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                  />
                </label>
                <button
                  type="button"
                  className={secondaryButton}
                  aria-label={`${t('leave.approve')}: ${r.studentName}`}
                  disabled={review.isPending}
                  onClick={() => review.mutate({ id: r.id, decision: 'approved' })}
                >
                  {t('leave.approve')}
                </button>
                <button
                  type="button"
                  className={secondaryButton}
                  aria-label={`${t('leave.reject')}: ${r.studentName}`}
                  disabled={review.isPending}
                  onClick={() => review.mutate({ id: r.id, decision: 'rejected' })}
                >
                  {t('leave.reject')}
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
      {list.data && list.data.meta.totalPages > 1 && (
        <Pager page={page} totalPages={list.data.meta.totalPages} onPage={setPage} />
      )}
    </div>
  );
}
